import {
  AttachFileRounded,
  ClearRounded,
  CloudUploadRounded,
  CloseRounded,
  DeleteOutlineRounded,
  DescriptionRounded,
  DownloadRounded,
  FilterAltRounded,
  FolderOpenRounded,
  ImageRounded,
  InsertDriveFileRounded,
  PeopleAltRounded,
  RefreshRounded,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { getTenants, type TenantRecord } from "../api/tenants";
import {
  deleteTenantFile,
  downloadTenantFile,
  getTenantFiles,
  openTenantFile,
  uploadTenantFile,
  type TenantFile,
} from "../api/admin-files";
import { useNotifications } from "../components/NotificationProvider";
import { ConfirmDialog } from "../components/ConfirmDialog";

const allowedTypes = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
];

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value: string) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

const getCurrentMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

export function FilesPage({
  token,
  readOnly = false,
}: {
  token: string;
  readOnly?: boolean;
}) {
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [files, setFiles] = useState<TenantFile[]>([]);
  const [tenantId, setTenantId] = useState<number | "">("");
  const [month, setMonth] = useState(getCurrentMonth);
  const [selectedTenantId, setSelectedTenantId] = useState<number | "">("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const [fileToDelete, setFileToDelete] = useState<TenantFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showAlert, showSnackbar } = useNotifications();

  const loadFiles = async () => {
    setIsLoading(true);
    try {
      setFiles(
        await getTenantFiles(token, {
          tenantId: tenantId || undefined,
          month: month || undefined,
        }),
      );
    } catch {
      showAlert({
        title: "Unable to load files",
        message: "Please try again shortly.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void getTenants()
      .then(setTenants)
      .catch(() => {
        showAlert({
          title: "Unable to load tenants",
          message: "The tenant list could not be loaded.",
          variant: "error",
        });
      });
  }, [showAlert]);

  useEffect(() => {
    const refreshTimer = window.setTimeout(() => {
      void loadFiles();
    }, 0);
    return () => window.clearTimeout(refreshTimer);
  }, [token, tenantId, month]);

  const selectFile = (file: File | undefined) => {
    if (!file) return;
    if (!allowedTypes.includes(file.type)) {
      setError("Choose an image, PDF, Word, Excel, or text file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Files must be smaller than 10MB.");
      return;
    }
    setError("");
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedTenantId || !selectedFile) return;
    setIsUploading(true);
    try {
      await uploadTenantFile(token, selectedTenantId, selectedFile);
      setSelectedFile(null);
      showSnackbar({
        title: "File uploaded",
        message: "The file is now stored against this tenant.",
        variant: "success",
      });
      await loadFiles();
    } catch (uploadError) {
      setError(
        uploadError instanceof Error ? uploadError.message : "Upload failed",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenFile = async (file: TenantFile) => {
    try {
      await openTenantFile(token, file.id);
    } catch {
      showAlert({
        title: "Unable to open file",
        message: "The file could not be loaded. Please try again.",
        variant: "error",
      });
    }
  };

  const handleDownloadFile = async (file: TenantFile) => {
    try {
      await downloadTenantFile(token, file.id, file.file_name);
    } catch {
      showAlert({
        title: "Unable to download file",
        message: "The file could not be downloaded. Please try again.",
        variant: "error",
      });
    }
  };

  const visibleLabel = useMemo(() => {
    if (tenantId)
      return (
        tenants.find((tenant) => tenant.id === tenantId)?.first_name ||
        "Selected tenant"
      );
    return "All tenants";
  }, [tenantId, tenants]);

  const hasFilters = Boolean(tenantId || month);

  const handleDelete = async () => {
    if (!fileToDelete) return;
    try {
      await deleteTenantFile(token, fileToDelete.id);
      setFiles((current) =>
        current.filter((item) => item.id !== fileToDelete.id),
      );
      showSnackbar({
        title: "File deleted",
        message: `${fileToDelete.file_name} was removed.`,
        variant: "info",
      });
      setFileToDelete(null);
    } catch {
      showAlert({
        title: "Delete failed",
        message: "The file could not be deleted.",
        variant: "error",
      });
    }
  };

  return (
    <Stack spacing={3}>
      <Paper
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          background: "linear-gradient(110deg, #102b4e 0%, #176b73 100%)",
          color: "white",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ md: "center" }}
        >
          <Box sx={{ maxWidth: 620 }}>
            <Chip
              size="small"
              icon={<FolderOpenRounded />}
              label="Tenant file library"
              sx={{
                mb: 1,
                color: "white",
                borderColor: "rgba(255,255,255,.35)",
              }}
              variant="outlined"
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, letterSpacing: "-.03em" }}
            >
              Every document, exactly where it belongs.
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: "rgba(255,255,255,.78)" }}
            >
              {readOnly
                ? "Browse and download tenant files."
                : "Upload once, then find records by tenant or month."}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Chip
              size="small"
              icon={<PeopleAltRounded />}
              label={`${tenants.length} tenants`}
              sx={{ color: "white", bgcolor: "rgba(255,255,255,.12)" }}
            />
            <Chip
              size="small"
              icon={<AttachFileRounded />}
              label={`${files.length} shown`}
              sx={{ color: "white", bgcolor: "rgba(255,255,255,.12)" }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2.5}
        alignItems="stretch"
      >
        {!readOnly && (
          <Card sx={{ flex: "0 0 350px", borderRadius: 3 }}>
            <CardContent sx={{ p: 2.25 }}>
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ mb: 1.75 }}
              >
                <CloudUploadRounded color="primary" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Add a file
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Admin upload
                  </Typography>
                </Box>
              </Stack>
              <Autocomplete
                size="small"
                options={tenants.filter((tenant) => !tenant.archived)}
                value={
                  tenants.find((tenant) => tenant.id === selectedTenantId) ??
                  null
                }
                onChange={(_, tenant) => setSelectedTenantId(tenant?.id ?? "")}
                getOptionLabel={(tenant) =>
                  `${tenant.first_name} ${tenant.last_name}`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign to tenant"
                    placeholder="Search tenants"
                  />
                )}
                sx={{ mb: 1.5 }}
              />
              <Box
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDragging(false);
                  selectFile(event.dataTransfer.files[0]);
                }}
                sx={{
                  p: 2,
                  textAlign: "center",
                  border: "2px dashed",
                  borderColor: isDragging ? "primary.main" : "divider",
                  bgcolor: isDragging ? "action.hover" : "grey.50",
                  borderRadius: 2,
                  cursor: "pointer",
                }}
              >
                <input
                  ref={fileInputRef}
                  hidden
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(event) => {
                    selectFile(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
                <InsertDriveFileRounded
                  sx={{ fontSize: 30, color: "primary.main" }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    mt: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedFile
                    ? selectedFile.name
                    : "Drop a file here or browse"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Images and office documents up to 10MB
                </Typography>
              </Box>
              {selectedFile && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    mt: 1,
                    px: 1.25,
                    py: 0.75,
                    bgcolor: "primary.50",
                    borderRadius: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Ready to upload: {selectedFile.name}
                  </Typography>
                  <Tooltip title="Remove selected file">
                    <IconButton
                      size="small"
                      onClick={() => setSelectedFile(null)}
                      aria-label="Remove selected file"
                    >
                      <CloseRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={<CloudUploadRounded />}
                disabled={!selectedTenantId || !selectedFile || isUploading}
                onClick={() => void handleUpload()}
                sx={{ mt: 1.5, py: 1 }}
              >
                {isUploading ? "Uploading..." : "Upload file"}
              </Button>
              {isUploading && <LinearProgress sx={{ mt: 1.5 }} />}
            </CardContent>
          </Card>
        )}

        <Paper
          variant="outlined"
          sx={{ flex: 1, p: { xs: 2, md: 3 }, borderRadius: 3 }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 1.75 }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterAltRounded color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  File library
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Showing {visibleLabel.toLowerCase()}{" "}
                {month ? `in ${month}` : "across all months"}
              </Typography>
            </Box>
            <Tooltip title="Refresh files">
              <IconButton
                size="small"
                onClick={() => void loadFiles()}
                aria-label="Refresh files"
                sx={{
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  mt: 0.25,
                }}
              >
                <RefreshRounded />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mb: 2.5 }}
          >
            <Autocomplete
              size="small"
              options={tenants}
              value={tenants.find((tenant) => tenant.id === tenantId) ?? null}
              onChange={(_, tenant) => setTenantId(tenant?.id ?? "")}
              getOptionLabel={(tenant) =>
                `${tenant.first_name} ${tenant.last_name}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tenant"
                  placeholder="All tenants"
                />
              )}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <TextField
                size="small"
                fullWidth
                label="Month"
                type="month"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Tooltip title="Clear month filter (show all time)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setMonth("")}
                    disabled={!month}
                    aria-label="Clear month filter"
                  >
                    <ClearRounded fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          {hasFilters && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                Filters active
              </Typography>
              <Button
                size="small"
                color="inherit"
                startIcon={<ClearRounded fontSize="small" />}
                onClick={() => {
                  setTenantId("");
                  setMonth("");
                }}
                sx={{ textTransform: "none", minWidth: 0, px: 0.75 }}
              >
                Show all files
              </Button>
            </Stack>
          )}
          {isLoading ? (
            <LinearProgress />
          ) : files.length === 0 ? (
            <Box sx={{ py: 7, textAlign: "center", color: "text.secondary" }}>
              <DescriptionRounded sx={{ fontSize: 48, color: "divider" }} />
              <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
                No files match these filters
              </Typography>
              <Typography variant="body2">
                {readOnly
                  ? "Try a different month or clear the filter."
                  : "Upload a tenant file or broaden your search."}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {files.map((file) => {
                const isImage = file.file_type.startsWith("image/");
                return (
                  <Card
                    key={file.id}
                    variant="outlined"
                    role="button"
                    tabIndex={0}
                    onClick={() => void handleOpenFile(file)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        void handleOpenFile(file);
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      cursor: "pointer",
                      transition:
                        "border-color 160ms ease, box-shadow 160ms ease",
                      "&:hover": { borderColor: "primary.light", boxShadow: 2 },
                      "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 1.5,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: isImage ? "success.50" : "primary.50",
                            color: isImage ? "success.main" : "primary.main",
                            flexShrink: 0,
                          }}
                        >
                          {isImage ? <ImageRounded /> : <DescriptionRounded />}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {file.file_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {file.tenant_name} · {formatSize(file.file_size)} ·{" "}
                            {formatDate(file.uploaded_at)}
                          </Typography>
                        </Box>
                        <Tooltip title="Download file">
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleDownloadFile(file);
                            }}
                            color="primary"
                            aria-label={`Download ${file.file_name}`}
                          >
                            <DownloadRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!readOnly && (
                          <Tooltip title="Delete file">
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                setFileToDelete(file);
                              }}
                              color="error"
                            >
                              <DeleteOutlineRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Paper>
      </Stack>
      {!readOnly && (
        <ConfirmDialog
          open={Boolean(fileToDelete)}
          title="Delete tenant file?"
          message={
            fileToDelete
              ? `This will permanently remove ${fileToDelete.file_name} from ${fileToDelete.tenant_name}'s file library.`
              : ""
          }
          confirmLabel="Delete file"
          onConfirm={() => void handleDelete()}
          onClose={() => setFileToDelete(null)}
        />
      )}
    </Stack>
  );
}
